import { useIonRouter, useIonViewWillEnter } from '@ionic/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ExternalLinkItemDto,
  GooglePlaceDto,
  UpdateLocationDto,
} from '@up2stay/up2promo-api-client';
import { ExternalLinkType, Permission } from '@up2stay/up2promo-api-client';
import { IntegrationsApi, LocationsApi } from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { addressDetailsFromPlace } from '@/core/components/locations/location-address-details';
import {
  isValidContactEmail,
  isValidContactPhone,
} from '@/core/components/locations/location-contact-validation';
import { useLocationDetailMutations } from '@/core/components/locations/use-location-detail-mutations';
import {
  ADDRESS_AUTOSAVE_MS,
  CONTACT_AUTOSAVE_MS,
  isBreakingExternalLinksChange,
  LINKS_AUTOSAVE_MS,
  NAME_AUTOSAVE_MS,
} from '@/core/components/locations/location-detail-autosave';
import { LOCATION_NAME_MAX_LENGTH } from '@/core/components/locations/location-detail-information-tab';
import {
  buildListHealthLabels,
  buildOverviewHealthLabels,
  buildReviewInsightsLabels,
} from '@/core/components/locations/location-detail-labels';
import {
  isValidFacebookUrl,
  isValidInstagramHandle,
  isValidMenuUrl,
  isValidTiktokHandle,
} from '@/core/components/locations/location-detail-external-services-tab';
import type { LocationOverviewTab } from '@/core/components/locations/location-detail-overview-tab';
import type { LocationDetailDict } from '@/core/components/locations/location-detail-types';
import type { LocationAddressDetails } from '@/core/components/locations/location-details-form';
import { getLocationHealthSummary } from '@/core/components/locations/location-health';
import {
  externalLinksFromLocation,
  type LocationLinkEntry,
  toExternalLinkItemDtos,
} from '@/core/components/locations/location-link-entry.types';
import {
  isMalformedLocationDetailPath,
  resolveLocationId,
} from '@/core/components/locations/resolve-location-id';
import { useAutosaveCue } from '@/core/hooks/use-autosave-cue';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useLocationLinkedProducts } from '@/core/hooks/use-location-linked-products';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import {
  useChannelLocations,
  useReputationOverview,
  useReputationSummary,
} from '@/core/hooks/use-reputation';
import {
  type Dictionary,
  useDictionary,
  useLocale,
} from '@/core/providers/dictionary-provider';
import { integrationsQueryKeys } from '@/core/query-keys/integrations-query-keys';
import { getApiErrorStatus } from '@/core/utils/api-error.utils';
import { locationsQueryKeys } from '@/core/query-keys/locations-query-keys';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

type UseLocationDetailOptions = {
  onDeleted?: () => void;
};

export function useLocationDetail({
  onDeleted,
}: UseLocationDetailOptions = {}) {
  const dict = useDictionary();
  const locale = useLocale();
  const ionRouter = useIonRouter();
  const queryClient = useQueryClient();
  const routeParams = useParams<{ id: string }>();
  const routerLocation = useLocation();
  const id = resolveLocationId(
    routeParams,
    ionRouter.routeInfo?.pathname,
    routerLocation.pathname,
  );
  const { navigateShell } = useNavigateShell();
  const currentWorkspace = useCurrentWorkspace();
  const { isFeatureEnabled } = useEntitlements();
  const reputationEnabled = isFeatureEnabled('feature.reputation');
  const { can } = usePermissions();
  const canManage = can(Permission.LOCATIONS_MANAGE);

  const d = (dict as { locations?: { detail?: LocationDetailDict } }).locations
    ?.detail;

  const {
    data: location,
    isLoading,
    isPending,
    isFetched,
    isError: isLocationQueryError,
    error: locationError,
    refetch: refetchLocation,
  } = useQuery({
    queryKey: locationsQueryKeys.detail(currentWorkspace?.id, id),
    queryFn: async () => {
      if (!currentWorkspace?.id || !id) return null;
      const response = await LocationsApi.findById({
        path: { workspaceId: currentWorkspace.id, id },
      });
      return response.data ?? null;
    },
    enabled: !!currentWorkspace?.id && !!id,
    staleTime: 30 * 1000,
  });

  useIonViewWillEnter(() => {
    void refetchLocation();
  });

  // A deleted or unknown location (404, malformed id 400) is "not found" with
  // a way back, not a load error whose retry can never help (DEV-558).
  const locationErrorStatus = getApiErrorStatus(locationError);
  const isError =
    isLocationQueryError &&
    locationErrorStatus !== 404 &&
    locationErrorStatus !== 400;

  const hasMalformedId =
    !id && isMalformedLocationDetailPath(routerLocation.pathname);

  const isResolvingLocation =
    !hasMalformedId &&
    (!id ||
      isPending ||
      isLoading ||
      (!!id && !!currentWorkspace?.id && !isFetched));

  const [linkEntries, setLinkEntries] = useState<LocationLinkEntry[]>([]);
  const [linkEntriesDirty, setLinkEntriesDirty] = useState(false);
  const {
    status: autosaveStatus,
    markSaving,
    markSaved,
    markError,
    markIdle,
  } = useAutosaveCue();

  const {
    deleteMutation,
    updateNameMutation,
    updateContactMutation,
    updateMenuAndInstagramMutation,
    updateFromPlaceMutation,
    updateAddressMutation,
    clearListingMutation,
  } = useLocationDetailMutations({
    workspaceId: currentWorkspace?.id,
    id,
    d,
    markSaved,
    markIdle,
    setLinkEntriesDirty,
    onDeleted,
  });

  const { data: integrationsData } = useQuery({
    queryKey: integrationsQueryKeys.all(currentWorkspace?.id),
    queryFn: async () => {
      if (!currentWorkspace?.id) return null;
      const response = await IntegrationsApi.list({
        path: { workspaceId: currentWorkspace.id },
      });
      return response.data ?? null;
    },
    // Free workspaces 403 here and would open the dismissible upgrade sheet.
    enabled: !!currentWorkspace?.id && isFeatureEnabled('feature.integrations'),
    staleTime: 60 * 1000,
  });

  const googleIntegration = integrationsData?.integrations?.find(
    (i) => i.provider === 'google',
  );
  const googleConnected = googleIntegration?.connected ?? false;

  const { data: channelLocations = [] } = useChannelLocations(
    reputationEnabled ? currentWorkspace?.id : undefined,
    { locationId: id },
  );

  const [activeTab, setActiveTab] = useState('overview');
  const [editNameValue, setEditNameValue] = useState('');
  const [contactEmailValue, setContactEmailValue] = useState('');
  const [contactPhoneValue, setContactPhoneValue] = useState('');
  const [contactEmailError, setContactEmailError] = useState<string | null>(
    null,
  );
  const [contactPhoneError, setContactPhoneError] = useState<string | null>(
    null,
  );
  /** Server contact values the fields were last synced from (DEV-557). */
  const syncedContactRef = useRef<{
    id: string;
    email: string;
    phone: string;
  } | null>(null);
  const [addressDetailsValue, setAddressDetailsValue] =
    useState<LocationAddressDetails | null>(null);
  const [latitudeValue, setLatitudeValue] = useState<number | undefined>(
    undefined,
  );
  const [longitudeValue, setLongitudeValue] = useState<number | undefined>(
    undefined,
  );
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [menuUrlError, setMenuUrlError] = useState<string | null>(null);
  const [instagramError, setInstagramError] = useState<string | null>(null);
  const [facebookError, setFacebookError] = useState<string | null>(null);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const [pendingExternalLinksPayload, setPendingExternalLinksPayload] =
    useState<UpdateLocationDto | null>(null);
  const [externalLinksConfirmOpen, setExternalLinksConfirmOpen] =
    useState(false);
  const saveNameTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveContactTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const saveAddressTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  /** Suppress address autosave until server sync after place search (avoids racing without placeId). */
  const suppressAddressAutosaveRef = useRef(false);
  const saveMenuAndInstagramTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  /**
   * Autosave only after field state has caught up to the loaded location
   * (microtask sync). Prevents enter-time no-op saves / Saved flash.
   */
  const [autosaveReadyId, setAutosaveReadyId] = useState<string | null>(null);

  useEffect(() => {
    const pending =
      updateNameMutation.isPending ||
      updateContactMutation.isPending ||
      updateAddressMutation.isPending ||
      updateFromPlaceMutation.isPending ||
      updateMenuAndInstagramMutation.isPending;
    if (pending) {
      markSaving();
    }
  }, [
    updateNameMutation.isPending,
    updateContactMutation.isPending,
    updateAddressMutation.isPending,
    updateFromPlaceMutation.isPending,
    updateMenuAndInstagramMutation.isPending,
    markSaving,
  ]);

  useEffect(() => {
    queueMicrotask(() => {
      setActiveTab('overview');
      setAddLinkOpen(false);
      setMenuUrlError(null);
      setInstagramError(null);
      setFacebookError(null);
      setTiktokError(null);
      setLinkEntriesDirty(false);
      setAutosaveReadyId(null);
    });
  }, [id]);

  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    const tab = params.get('tab');
    queueMicrotask(() => {
      if (tab === 'integrations') setActiveTab('integrations');
      if (tab === 'external-services') setActiveTab('external-services');
      if (tab === 'ai-replies' && id) {
        navigateShell(
          `${shellPaths.reputationSetup}?locationId=${id}&tab=responses`,
        );
      }
    });
  }, [routerLocation.search, id, navigateShell]);

  useEffect(() => {
    if (location?.name != null) {
      queueMicrotask(() => setEditNameValue(location.name));
    }
  }, [location?.id, location?.name]);

  const serverContactEmail = location?.contactEmail ?? '';
  const serverContactPhone = location?.contactPhone ?? '';
  useEffect(() => {
    if (!location?.id) return;
    const next = {
      id: location.id,
      email: serverContactEmail,
      phone: serverContactPhone,
    };
    const prev = syncedContactRef.current;
    syncedContactRef.current = next;
    const switched = prev?.id !== next.id;
    // Take over only the field whose server value changed: the refetch after
    // saving the email must not wipe the phone number being typed (DEV-557).
    queueMicrotask(() => {
      if (switched || prev?.email !== next.email) {
        setContactEmailValue(next.email);
      }
      if (switched || prev?.phone !== next.phone) {
        setContactPhoneValue(next.phone);
      }
      if (switched) {
        setContactEmailError(null);
        setContactPhoneError(null);
      }
    });
  }, [location?.id, serverContactEmail, serverContactPhone]);

  useEffect(() => {
    const loc = location;
    if (!loc) return;
    queueMicrotask(() => {
      const details = loc.addressDetails;
      setAddressDetailsValue(
        details
          ? {
              formatted: details.formatted ?? loc.address ?? null,
              postalCode: details.postalCode ?? null,
              street: details.street ?? null,
              streetNumber: details.streetNumber ?? null,
              locality: details.locality ?? null,
              administrativeArea: details.administrativeArea ?? null,
              administrativeAreaLevel2:
                details.administrativeAreaLevel2 ?? null,
              country: details.country ?? null,
              countryCode: details.countryCode ?? null,
            }
          : loc.address
            ? { formatted: loc.address }
            : null,
      );
      setLatitudeValue(loc.coordinates?.latitude);
      setLongitudeValue(loc.coordinates?.longitude);
      suppressAddressAutosaveRef.current = false;
    });
  }, [
    location?.id,
    location?.address,
    location?.addressDetails,
    location?.coordinates?.latitude,
    location?.coordinates?.longitude,
    location?.reviewExternalIds,
    location,
  ]);

  const serverExternalLinksKey = location?.externalLinks
    ? JSON.stringify(
        location.externalLinks.map((e) => ({
          type: e.type,
          value: e.value ?? '',
        })),
      )
    : '';

  useEffect(() => {
    if (!location?.id || linkEntriesDirty) return;
    queueMicrotask(() => {
      setLinkEntries(externalLinksFromLocation(location.externalLinks));
    });
  }, [
    location?.id,
    serverExternalLinksKey,
    location?.externalLinks,
    linkEntriesDirty,
  ]);

  // Arm autosave after the hydrate microtasks above have applied server fields.
  useEffect(() => {
    if (!location?.id) {
      queueMicrotask(() => {
        setAutosaveReadyId(null);
      });
      return;
    }
    const readyId = location.id;
    queueMicrotask(() => {
      setAutosaveReadyId(readyId);
    });
  }, [
    location?.id,
    location?.name,
    location?.contactEmail,
    location?.contactPhone,
    location?.address,
    location?.addressDetails,
    location?.coordinates?.latitude,
    location?.coordinates?.longitude,
    serverExternalLinksKey,
  ]);

  const saveName = useCallback(() => {
    if (
      !location ||
      !editNameValue.trim() ||
      editNameValue.trim() === location.name
    ) {
      return;
    }
    const next = editNameValue.trim().slice(0, LOCATION_NAME_MAX_LENGTH);
    markSaving();
    updateNameMutation.mutate(next);
  }, [location, editNameValue, updateNameMutation, markSaving]);

  const flushNameSave = useCallback(() => {
    if (saveNameTimeoutRef.current) {
      clearTimeout(saveNameTimeoutRef.current);
      saveNameTimeoutRef.current = null;
    }
    if (!editNameValue.trim() && location) {
      setEditNameValue(location.name);
      return;
    }
    saveName();
  }, [editNameValue, location, saveName]);

  useEffect(() => {
    if (!location?.id) return;
    if (autosaveReadyId !== location.id) return;
    if (saveNameTimeoutRef.current) {
      clearTimeout(saveNameTimeoutRef.current);
    }
    saveNameTimeoutRef.current = setTimeout(() => {
      saveName();
    }, NAME_AUTOSAVE_MS);
    return () => {
      if (saveNameTimeoutRef.current) {
        clearTimeout(saveNameTimeoutRef.current);
      }
    };
  }, [editNameValue, location?.id, autosaveReadyId, saveName]);

  const saveContact = useCallback(() => {
    if (!location?.id) return;
    const email = contactEmailValue.trim();
    const phone = contactPhoneValue.trim();
    const emailChanged = email !== (location.contactEmail ?? '');
    const phoneChanged = phone !== (location.contactPhone ?? '');
    if (!emailChanged && !phoneChanged) {
      setContactEmailError(null);
      setContactPhoneError(null);
      return;
    }
    // Like the links tab: an invalid field blocks the save and says why at
    // the field (DEV-557). Empty clears the value.
    const emailInvalid = emailChanged && !!email && !isValidContactEmail(email);
    const phoneInvalid = phoneChanged && !!phone && !isValidContactPhone(phone);
    setContactEmailError(
      emailInvalid
        ? (d?.contact?.emailInvalid ?? 'Please enter a valid email address')
        : null,
    );
    setContactPhoneError(
      phoneInvalid
        ? (d?.contact?.phoneInvalid ?? 'Please enter a valid phone number')
        : null,
    );
    if (emailInvalid || phoneInvalid) {
      markError();
      return;
    }
    markSaving();
    updateContactMutation.mutate({
      ...(emailChanged ? { contactEmail: email } : {}),
      ...(phoneChanged ? { contactPhone: phone } : {}),
    });
  }, [
    location,
    contactEmailValue,
    contactPhoneValue,
    updateContactMutation,
    markSaving,
    markError,
    d?.contact?.emailInvalid,
    d?.contact?.phoneInvalid,
  ]);

  const changeContactEmail = useCallback((value: string) => {
    setContactEmailValue(value);
    setContactEmailError(null);
  }, []);

  const changeContactPhone = useCallback((value: string) => {
    setContactPhoneValue(value);
    setContactPhoneError(null);
  }, []);

  const flushContactSave = useCallback(() => {
    if (saveContactTimeoutRef.current) {
      clearTimeout(saveContactTimeoutRef.current);
      saveContactTimeoutRef.current = null;
    }
    saveContact();
  }, [saveContact]);

  useEffect(() => {
    if (!location?.id) return;
    if (autosaveReadyId !== location.id) return;
    if (saveContactTimeoutRef.current)
      clearTimeout(saveContactTimeoutRef.current);
    saveContactTimeoutRef.current = setTimeout(() => {
      saveContactTimeoutRef.current = null;
      saveContact();
    }, CONTACT_AUTOSAVE_MS);
    return () => {
      if (saveContactTimeoutRef.current)
        clearTimeout(saveContactTimeoutRef.current);
    };
  }, [
    contactEmailValue,
    contactPhoneValue,
    location?.id,
    autosaveReadyId,
    saveContact,
  ]);

  const saveAddress = useCallback(() => {
    if (!location?.id) return;
    const serverFormatted =
      location.addressDetails?.formatted ?? location.address ?? '';
    const nextFormatted = addressDetailsValue?.formatted ?? '';
    const serverLat = location.coordinates?.latitude;
    const serverLng = location.coordinates?.longitude;
    const sameAddress =
      serverFormatted.trim() === nextFormatted.trim() &&
      (location.addressDetails?.postalCode ?? null) ===
        (addressDetailsValue?.postalCode ?? null) &&
      (location.addressDetails?.street ?? null) ===
        (addressDetailsValue?.street ?? null) &&
      (location.addressDetails?.streetNumber ?? null) ===
        (addressDetailsValue?.streetNumber ?? null) &&
      (location.addressDetails?.locality ?? null) ===
        (addressDetailsValue?.locality ?? null) &&
      (location.addressDetails?.administrativeArea ?? null) ===
        (addressDetailsValue?.administrativeArea ?? null) &&
      (location.addressDetails?.administrativeAreaLevel2 ?? null) ===
        (addressDetailsValue?.administrativeAreaLevel2 ?? null) &&
      (location.addressDetails?.country ?? null) ===
        (addressDetailsValue?.country ?? null) &&
      (location.addressDetails?.countryCode ?? null) ===
        (addressDetailsValue?.countryCode ?? null);
    const sameCoords =
      serverLat === latitudeValue && serverLng === longitudeValue;
    if (sameAddress && sameCoords) return;
    markSaving();
    updateAddressMutation.mutate({
      addressDetails: addressDetailsValue,
      latitude: latitudeValue,
      longitude: longitudeValue,
    });
  }, [
    location,
    addressDetailsValue,
    latitudeValue,
    longitudeValue,
    updateAddressMutation,
    markSaving,
  ]);

  const flushAddressSave = useCallback(() => {
    if (suppressAddressAutosaveRef.current) return;
    if (saveAddressTimeoutRef.current) {
      clearTimeout(saveAddressTimeoutRef.current);
      saveAddressTimeoutRef.current = null;
    }
    saveAddress();
  }, [saveAddress]);

  const handleAddressPlaceSelect = useCallback(
    (place: GooglePlaceDto) => {
      suppressAddressAutosaveRef.current = true;
      if (saveAddressTimeoutRef.current) {
        clearTimeout(saveAddressTimeoutRef.current);
        saveAddressTimeoutRef.current = null;
      }
      markSaving();
      updateAddressMutation.mutate(
        {
          addressDetails: addressDetailsFromPlace(place),
          latitude: place.latitude ?? undefined,
          longitude: place.longitude ?? undefined,
        },
        {
          onError: () => {
            suppressAddressAutosaveRef.current = false;
          },
        },
      );
    },
    [updateAddressMutation, markSaving],
  );

  useEffect(() => {
    if (!location?.id) return;
    if (autosaveReadyId !== location.id) return;
    if (suppressAddressAutosaveRef.current) return;
    if (saveAddressTimeoutRef.current)
      clearTimeout(saveAddressTimeoutRef.current);
    saveAddressTimeoutRef.current = setTimeout(() => {
      saveAddressTimeoutRef.current = null;
      if (suppressAddressAutosaveRef.current) return;
      saveAddress();
    }, ADDRESS_AUTOSAVE_MS);
    return () => {
      if (saveAddressTimeoutRef.current)
        clearTimeout(saveAddressTimeoutRef.current);
    };
  }, [
    addressDetailsValue,
    latitudeValue,
    longitudeValue,
    location?.id,
    autosaveReadyId,
    saveAddress,
  ]);

  const { data: linkedProducts = [] } = useLocationLinkedProducts(
    currentWorkspace?.id,
    id,
  );

  const linkedProductsDict = d?.linkedProducts;

  const locationHealth = useMemo(() => {
    if (!location) return null;
    return getLocationHealthSummary(location, channelLocations);
  }, [location, channelLocations]);

  const reviewCapabilityActive =
    locationHealth?.google === 'linked' || locationHealth?.google === 'listing';
  const showReviewInsights = reputationEnabled && reviewCapabilityActive;

  const { data: reputationOverview, isLoading: reputationOverviewLoading } =
    useReputationOverview(
      showReviewInsights && locationHealth?.google === 'linked'
        ? currentWorkspace?.id
        : undefined,
      { locationId: id },
    );

  const hasReviewsForSummary =
    showReviewInsights &&
    locationHealth?.google === 'linked' &&
    !!reputationOverview &&
    reputationOverview.totalReviews > 0;

  const {
    data: reputationSummary,
    isLoading: reputationSummaryLoading,
    isError: reputationSummaryError,
  } = useReputationSummary(
    hasReviewsForSummary ? currentWorkspace?.id : undefined,
    { locationId: id },
    hasReviewsForSummary,
  );

  const overviewHealthLabels = useMemo(() => buildOverviewHealthLabels(d), [d]);
  const listHealthLabels = useMemo(
    () => buildListHealthLabels(overviewHealthLabels),
    [overviewHealthLabels],
  );
  const reviewInsightsLabels = useMemo(
    () => buildReviewInsightsLabels(d, dict as Dictionary),
    [d, dict],
  );

  const handleOverviewNavigate = useCallback((tab: LocationOverviewTab) => {
    setActiveTab(tab);
  }, []);

  const handleOpenReputation = useCallback(() => {
    if (!id) return;
    navigateShell(
      `${shellPaths.reputationAnalytics}?${new URLSearchParams({ locationId: id }).toString()}`,
    );
  }, [id, navigateShell]);

  const handleOpenReputationInbox = useCallback(() => {
    if (!id) return;
    navigateShell(
      `${shellPaths.reputationInbox}?${new URLSearchParams({ locationId: id }).toString()}`,
    );
  }, [id, navigateShell]);

  const applyExternalLinksUpdate = useCallback(
    (payload: UpdateLocationDto) => {
      updateMenuAndInstagramMutation.mutate(payload);
    },
    [updateMenuAndInstagramMutation],
  );

  const saveMenuAndInstagram = useCallback(() => {
    if (!location?.id) return;

    setMenuUrlError(null);
    setInstagramError(null);
    setFacebookError(null);
    setTiktokError(null);
    for (const entry of linkEntries) {
      if (entry.type === ExternalLinkType.MENU && entry.value.trim()) {
        if (!isValidMenuUrl(entry.value)) {
          setMenuUrlError(
            d?.externalServices?.menuUrlInvalid ??
              'Please enter a valid URL (e.g. https://…)',
          );
          return;
        }
      }
      if (entry.type === ExternalLinkType.INSTAGRAM && entry.value.trim()) {
        if (!isValidInstagramHandle(entry.value)) {
          setInstagramError(
            d?.externalServices?.instagramInvalid ??
              'Use only letters, numbers, dots and underscores',
          );
          return;
        }
      }
      if (entry.type === ExternalLinkType.FACEBOOK && entry.value.trim()) {
        if (!isValidFacebookUrl(entry.value)) {
          setFacebookError(
            d?.externalServices?.facebookInvalid ??
              'Please enter a valid URL (e.g. https://…)',
          );
          return;
        }
      }
      if (entry.type === ExternalLinkType.TIKTOK && entry.value.trim()) {
        if (!isValidTiktokHandle(entry.value)) {
          setTiktokError(
            d?.externalServices?.tiktokInvalid ??
              'Use only letters, numbers, dots and underscores',
          );
          return;
        }
      }
    }

    const externalLinks: ExternalLinkItemDto[] = toExternalLinkItemDtos(
      linkEntries.filter((e) => e.type),
    );

    const existing =
      location.externalLinks?.map((e) => ({
        type: e.type,
        value: (e.value ?? '').trim(),
      })) ?? [];
    const same =
      existing.length === externalLinks.length &&
      existing.every(
        (e, i) =>
          e.type === externalLinks[i]?.type &&
          e.value === externalLinks[i]?.value,
      );
    if (same) return;

    const payload: UpdateLocationDto = { externalLinks };

    if (
      linkedProducts.length > 0 &&
      isBreakingExternalLinksChange(existing, externalLinks, linkedProducts)
    ) {
      setPendingExternalLinksPayload(payload);
      setExternalLinksConfirmOpen(true);
      return;
    }

    markSaving();
    applyExternalLinksUpdate(payload);
  }, [
    applyExternalLinksUpdate,
    d?.externalServices?.menuUrlInvalid,
    d?.externalServices?.instagramInvalid,
    d?.externalServices?.facebookInvalid,
    d?.externalServices?.tiktokInvalid,
    linkedProducts,
    location,
    linkEntries,
    markSaving,
  ]);

  useEffect(() => {
    if (!location?.id) return;
    if (autosaveReadyId !== location.id) return;
    if (saveMenuAndInstagramTimeoutRef.current)
      clearTimeout(saveMenuAndInstagramTimeoutRef.current);
    saveMenuAndInstagramTimeoutRef.current = setTimeout(() => {
      saveMenuAndInstagramTimeoutRef.current = null;
      saveMenuAndInstagram();
    }, LINKS_AUTOSAVE_MS);
    return () => {
      if (saveMenuAndInstagramTimeoutRef.current)
        clearTimeout(saveMenuAndInstagramTimeoutRef.current);
    };
  }, [linkEntries, location?.id, autosaveReadyId, saveMenuAndInstagram]);

  const handleLinkEntriesChange = useCallback(
    (
      entries:
        | LocationLinkEntry[]
        | ((prev: LocationLinkEntry[]) => LocationLinkEntry[]),
    ) => {
      setLinkEntriesDirty(true);
      setLinkEntries(entries);
    },
    [],
  );

  const handleExternalLinksConfirmCancel = useCallback(() => {
    setPendingExternalLinksPayload(null);
    setLinkEntriesDirty(false);
    if (location?.externalLinks) {
      setLinkEntries(externalLinksFromLocation(location.externalLinks));
    }
  }, [location]);

  const handleExternalLinksConfirmSave = useCallback(() => {
    if (pendingExternalLinksPayload) {
      markSaving();
      applyExternalLinksUpdate(pendingExternalLinksPayload);
    }
    setPendingExternalLinksPayload(null);
    setExternalLinksConfirmOpen(false);
  }, [pendingExternalLinksPayload, markSaving, applyExternalLinksUpdate]);

  const openProductsForLocation = useCallback(() => {
    if (!location?.id) return;
    navigateShell(
      `${shellPaths.moreProducts}?locationId=${encodeURIComponent(location.id)}`,
    );
  }, [location, navigateShell]);

  return {
    dict,
    locale,
    d,
    id,
    currentWorkspace,
    location,
    isResolvingLocation,
    isError,
    refetchLocation,
    autosaveStatus,
    canManage,
    deleteMutation,
    updateFromPlaceMutation,
    clearListingMutation,
    googleConnected,
    activeTab,
    setActiveTab,
    editNameValue,
    setEditNameValue,
    flushNameSave,
    contactEmailValue,
    changeContactEmail,
    contactEmailError,
    contactPhoneValue,
    changeContactPhone,
    contactPhoneError,
    flushContactSave,
    addressDetailsValue,
    setAddressDetailsValue,
    flushAddressSave,
    latitudeValue,
    longitudeValue,
    setLatitudeValue,
    setLongitudeValue,
    handleAddressPlaceSelect,
    linkEntries,
    handleLinkEntriesChange,
    addLinkOpen,
    setAddLinkOpen,
    menuUrlError,
    setMenuUrlError,
    instagramError,
    setInstagramError,
    facebookError,
    setFacebookError,
    tiktokError,
    setTiktokError,
    linkedProducts,
    linkedProductsDict,
    locationHealth,
    showReviewInsights,
    reputationEnabled,
    reputationOverview,
    reputationOverviewLoading,
    reputationSummary,
    reputationSummaryLoading,
    reputationSummaryError,
    overviewHealthLabels,
    listHealthLabels,
    reviewInsightsLabels,
    handleOverviewNavigate,
    handleOpenReputation,
    handleOpenReputationInbox,
    openProductsForLocation,
    navigateShell,
    externalLinksConfirmOpen,
    setExternalLinksConfirmOpen,
    handleExternalLinksConfirmCancel,
    handleExternalLinksConfirmSave,
  };
}

export type UseLocationDetailReturn = ReturnType<typeof useLocationDetail>;
